import styled from 'styled-components'
import { useCallback, useEffect, useState } from 'react'
import { TextId, useTextsV2 } from '../../utils/useTexts'

// IMPORTANT: THIS SVG MUST BE IMPORTED AS A DATA URL, not like a react
// component. The reason seems to be that this file contains id attributes and
// references that could cause rendering errors when using multiple instances of
// this SVG.
import ScaniaSymbol from '../../assets/img/scania-symbol.svg'
import {
  SvgInfo,
  SvgProfile,
  SvgScaniaWordmark,
  SvgTriangle,
} from '../SvgImports'
import { TestElementTypeId } from '../../types/TestAttributeId'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { BreakpointWidthPx } from '../../css/BreakpointWidthPx'
import {
  closeGvPanel,
  openGvPanel,
  getSidePanelState,
  setGvSidePanelContent,
} from '../../store/sidePanelSlice'
import {
  getMarketLanguageState,
  getMarketSettingsState,
} from '../../store/appSlice'
import { toggleMainMenuMobile } from '../../store/menuSlice'
import { TdsHeaderHamburger } from '@scania/tegel-react'
import { SidePanelContent } from '../../store/types'
import {
  getGuidedViewPage,
  getIsLoggedInState,
} from '../../store/sessionDataSlice'
import { BreakpointHeightPx } from '../../css/BreakpointHeightPx'
import { getLanguageName } from '../../utils/languageLookup'
import { buildRelativeLandingPageUrl } from '../../utils/UrlBuilders'
import { EtelMarketAndLanguage } from '../../api/startup'
import { getUrlParametersToString } from '../../utils/getUrlParametersToString'

const HeaderRoot = styled.header`
  display: flex;
  justify-content: space-between;
  height: var(--header-height);
  min-height: var(--header-height);
  background: var(--tds-blue-800) !important;
`

const DesktopContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  margin-left: 24px;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: none;
  }
`

const LogotypeWrapper = styled.div`
  cursor: pointer;
  svg {
    height: 22px;
    width: auto;
    path {
      fill: var(--tds-white);
    }
  }
`

const Separator = styled.div`
  height: 2em;
  width: 1px;
  background-color: var(--tds-blue-700);
  margin: 0 16px;
  display: block;
`

const Market = styled.div`
  font-family: 'Scania Sans Headline', Helvetica, Arial, sans-serif;
  white-space: nowrap;
  font-size: 14px;
  text-transform: uppercase;
  color: var(--tds-white);
  opacity: 60%;
  cursor: default;
  display: block;
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 64px;
  cursor: pointer;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: var(--tds-blue-700);
    }
  }

  svg {
    height: 20px;
    width: 20px;
    color: var(--tds-white);
  }
`

const LogoSymbolWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 64px;
  cursor: pointer;
  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    width: 20%;
  }
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: var(--tds-blue-700);
    }
  }
  img {
    width: 32px;
  }
`

const LanguageSelectWrapper = styled.div`
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      svg {
        rotate: 0deg;
      }

      #LanguagePopUp {
        display: block;
      }
    }
  }

  svg {
    height: 16px;
    width: 16px;
    fill: white;
    rotate: 180deg;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    margin-left: 16px;
  }
`

const Language = styled.div`
  color: white;
  font-size: 16px;
  margin-right: 4px;
  text-transform: uppercase;
`

interface LanguagePopUpInterface {
  $showUserIcon: boolean
}

const LanguagePopUp = styled.div<LanguagePopUpInterface>`
  position: absolute;
  background-color: white;
  top: 64px;
  z-index: 2;
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.2);
  display: none;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: block;
    left: 0;
  }
`

const LanguagePopUpBox = styled.div`
  font-size: 18px;
  padding: 10px 20px;
  text-transform: uppercase;
  border-bottom: 1px solid var(--tds-grey-300);

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
      background-color: var(--tds-blue-800);
      color: white;
    }
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    font-size: 20px;
  }
`

interface Mobilecontainerprops {
  $showLeftMenuIcon: boolean
}

const MobileContainer = styled.div<Mobilecontainerprops>`
  display: none;
  width: 100%;
  height: 100%;
  justify-content: ${(props) =>
    props.$showLeftMenuIcon ? 'space-between' : 'flex-end'};

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: flex;
  }
`

const HamburgerWrapper = styled.div`
  display: none;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: unset;
  }
`

const MobileContainerRight = styled.div`
  display: flex;
  justify-content: flex-end;
`

interface ShowIcon {
  $isGuidedView: boolean
}

const IconContainerPortrait = styled.div<ShowIcon>`
  width: 64px;
  min-width: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: var(--tds-blue-700);
    }
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: ${(props) => (props.$isGuidedView ? 'none' : 'flex')};
  }

  svg {
    height: 20px;
    width: 20px;
    color: var(--tds-white);
  }
`

const SeparatorFullHeigth = styled.div`
  height: 64px;
  width: 1px;
  background-color: var(--tds-blue-700);
`

export function AppHeader() {
  const dispatch = useAppDispatch()
  const t2 = useTextsV2()
  const marketSettings = useAppSelector(getMarketSettingsState)
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const showLanguageSelector =
    marketSettings?.languages && marketSettings?.languages.length > 1
      ? true
      : false
  const isLoggedIn = useAppSelector(getIsLoggedInState)
  const guidedViewPage = useAppSelector(getGuidedViewPage)
  const showUserButton = marketSettings?.garageEnabled
  const sidePanelIsOpen = useAppSelector(getSidePanelState).gvSidePanelOpen
  const isFirstPage =
    window.location.pathname === '/' ||
    (window.location.pathname.includes('factory-models') &&
      marketSettings?.factoryModelsMode === 1)
  const [showPopUpMobile, setShowPopUpMobile] = useState<boolean>(false)

  const handleMainMenuIconClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      dispatch(toggleMainMenuMobile())
      dispatch(closeGvPanel())
      dispatch(setGvSidePanelContent(null))
    },
    [dispatch],
  )

  const onAccountClick = useCallback(
    (e: React.MouseEvent) => {
      if (!sidePanelIsOpen) {
        e.stopPropagation()
        if (isLoggedIn) {
          dispatch(openGvPanel())
          dispatch(setGvSidePanelContent(SidePanelContent.garage))
        } else {
          dispatch(openGvPanel())
          dispatch(setGvSidePanelContent(SidePanelContent.login))
        }
      } else {
        dispatch(closeGvPanel())
        dispatch(setGvSidePanelContent(null))
      }
    },
    [dispatch, isLoggedIn, sidePanelIsOpen],
  )

  const onInfoClick = useCallback(
    (e: React.MouseEvent) => {
      if (!sidePanelIsOpen) {
        e.stopPropagation()
        dispatch(openGvPanel())
        dispatch(setGvSidePanelContent(SidePanelContent.info))
      } else {
        dispatch(closeGvPanel())
        dispatch(setGvSidePanelContent(null))
      }
    },
    [dispatch, sidePanelIsOpen],
  )

  const handleLanguageSelect = (etelLanguage: number) => {
    const etelMarket = marketLanguage?.market
    if (!etelMarket) {
      throw new Error('Expected etel market id.')
    }

    if (!etelLanguage) {
      throw new Error('Expected etel language id.')
    }

    const marketLanguageInput: EtelMarketAndLanguage = {
      market: etelMarket,
      language: etelLanguage,
    }
    const relativeUrl = buildRelativeLandingPageUrl(marketLanguageInput)
    const params = relativeUrl.params
    const restartUrl = relativeUrl.path + getUrlParametersToString(params)
    document.location.href = restartUrl
  }

  const handleLanguageSelectorClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowPopUpMobile(!showPopUpMobile)
  }

  const handleLanguageDropDownClose = useCallback((e: MouseEvent) => {
    const languagePopUpMobile = document.getElementById('LanguagePopUpMobile')
    if (
      languagePopUpMobile &&
      !languagePopUpMobile.contains(e.target as Node)
    ) {
      setShowPopUpMobile(false)
    }
  }, [])

  // Eventlistener to close pop up for mobile on click outside pop up
  useEffect(() => {
    document.addEventListener('click', handleLanguageDropDownClose)
    return () => {
      document.removeEventListener('click', handleLanguageDropDownClose)
    }
  }, [handleLanguageDropDownClose])

  return (
    <HeaderRoot data-name="Header">
      <MobileContainer
        $showLeftMenuIcon={guidedViewPage !== null || isFirstPage}
      >
        <HamburgerWrapper>
          {guidedViewPage !== null && (
            <TdsHeaderHamburger
              onClick={handleMainMenuIconClick}
              aria-expanded="true"
              aria-label="test"
              aria-haspopup="true"
            />
          )}
          {showLanguageSelector && isFirstPage && (
            <LanguageSelectWrapper onClick={handleLanguageSelectorClick}>
              <Language>{marketLanguage?.isoLanguageCode}</Language>
              <SvgTriangle />
              {showPopUpMobile && (
                <LanguagePopUp
                  $showUserIcon={showUserButton || false}
                  id="LanguagePopUpMobile"
                >
                  {marketSettings?.languages.map((language) => (
                    <LanguagePopUpBox
                      onClick={() =>
                        handleLanguageSelect(language.etelLanguage)
                      }
                      key={language.etelLanguage}
                    >
                      {getLanguageName(language.isoCode)}
                    </LanguagePopUpBox>
                  ))}
                </LanguagePopUp>
              )}
            </LanguageSelectWrapper>
          )}
        </HamburgerWrapper>
        <MobileContainerRight>
          {showUserButton && (
            <IconContainerPortrait
              onClick={onAccountClick}
              $isGuidedView={guidedViewPage !== null}
            >
              <SvgProfile />
            </IconContainerPortrait>
          )}
          <IconContainerPortrait
            onClick={onInfoClick}
            $isGuidedView={guidedViewPage !== null}
          >
            <SvgInfo />
          </IconContainerPortrait>
          <LogoSymbolWrapper style={{ width: '64px' }}>
            <a
              href={t2(TextId.LINK_WORD_MARK)}
              target={'_blank'}
              rel="noreferrer"
            >
              <img src={ScaniaSymbol} alt="Scania Symbol" />
            </a>
          </LogoSymbolWrapper>
        </MobileContainerRight>
      </MobileContainer>

      <DesktopContainer>
        <LogotypeWrapper
          data-test-element-type={TestElementTypeId.HeaderLogoType}
        >
          <a
            href={t2(TextId.LINK_WORD_MARK)}
            target={'_blank'}
            rel="noreferrer"
          >
            <SvgScaniaWordmark title="Home" />
          </a>
        </LogotypeWrapper>
        <Separator />
        <Market data-test-element-type={TestElementTypeId.HeaderMarketName}>
          {t2(TextId.MARKET)}
        </Market>
      </DesktopContainer>
      <DesktopContainer>
        {showLanguageSelector && isFirstPage && (
          <LanguageSelectWrapper>
            <Language>{marketLanguage?.isoLanguageCode}</Language>
            <SvgTriangle />
            <LanguagePopUp
              $showUserIcon={showUserButton || false}
              id="LanguagePopUp"
            >
              {marketSettings?.languages.map((language) => (
                <LanguagePopUpBox
                  onClick={() => handleLanguageSelect(language.etelLanguage)}
                  key={language.etelLanguage}
                >
                  {getLanguageName(language.isoCode)}
                </LanguagePopUpBox>
              ))}
            </LanguagePopUp>
          </LanguageSelectWrapper>
        )}
        <SeparatorFullHeigth style={{ display: 'none' }} />
        {showUserButton && (
          <>
            <SeparatorFullHeigth />
            <IconContainer
              data-test-element-type={TestElementTypeId.HeaderAccountIcon}
              className="account"
              onClick={onAccountClick}
            >
              <SvgProfile />
            </IconContainer>
          </>
        )}
        <SeparatorFullHeigth />
        <IconContainer
          data-test-element-type={TestElementTypeId.HeaderInfoIcon}
          onClick={onInfoClick}
        >
          <SvgInfo />
        </IconContainer>
        <SeparatorFullHeigth />
        <LogoSymbolWrapper
          data-test-element-type={TestElementTypeId.HeaderLogoTypeIcon}
        >
          <a
            href={t2(TextId.LINK_WORD_MARK)}
            target={'_blank'}
            rel="noreferrer"
          >
            <img src={ScaniaSymbol} alt="Scania Symbol" />
          </a>
        </LogoSymbolWrapper>
      </DesktopContainer>
    </HeaderRoot>
  )
}
